/**
 * services/image-resizer
 */

import Promise from 'bluebird';
import Pica from 'pica';
import { omit } from 'lodash';
import loader from '@gaoding/editor-utils/loader';

import dataURLtoBlob from './dataurl-to-blob';

/**
 * 加载图片
 * @param {HTMLImageElement|string} img 图片地址或图片元素
 */
function loadImage(img) {
    if(typeof img === 'string') {
        return loader._loadImage(img);
    }
    else {
        return new Promise((resolve, reject) => {
            if(img.complete) {
                // IE BUG: 当图片地址为 dataURL 时，complete 状态可能并未完成渲染
                setTimeout(() => {
                    resolve(img);
                }, 0);

                return;
            }

            img.onload = () => {
                img.onload = img.onerror = null;

                resolve(img);
            };

            img.onerror = () => {
                img.onload = img.onerror = null;

                reject(new Error('Image load error'));
            };
        });
    }
}
const pica = new Pica();
const resizeByPica = async(image, options) => {
    const canvas = document.createElement('canvas');
    const picaOptions = omit(options, ['width', 'height', 'type', 'quality']);
    canvas.width = Math.max(1, options.width);
    canvas.height = Math.max(1, options.height);
    await pica.resize(image, canvas, picaOptions);
    return pica.toBlob(canvas, `image/${options.type}`, options.quality);
};

const readFileToImage = file => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = () => {
            resolve(reader.result);
        };

        reader.onerror = () => {
            reject(new Error('File load error'));
        };
        reader.readAsDataURL(file.rawFile || file);
    })
        .then(url => {
            return loadImage(url);
        });
};

const imageResizer = {
    getResizeSize(width, height, options) {
        let { maxSize, maxWidth, maxHeight, minWidth, minHeight } = Object.assign({
            maxSize: 2000 * 2000,
            minWidth: 0,
            minHeight: 0,
        }, options);

        if(width < minWidth || height < minHeight) {
            throw new Error('too small');
        }
        let w = width;
        let h = height;
        // 默认以 maxSize 压缩
        if(maxWidth || maxHeight) {
            const whRatio = width / height;
            maxWidth = maxWidth || maxHeight;
            maxHeight = maxHeight || maxWidth;

            if(whRatio >= 1) {
                w = Math.min(width, maxWidth);
                h = Math.round(height * (w / width));
            }
            else {
                h = Math.min(height, maxHeight);
                w = Math.round(width * (h / height));
            }
        }
        else if(maxSize > 0) {
            let size = width * height;
            let ratio = Math.sqrt(Math.min(1, maxSize / size));

            h = height * ratio;
            w = width * ratio;
        }

        if(w !== width || h !== height) {
            return [ w, h ];
        }

        return false;
    },
    resizeImage(img, {
        type = 'jpeg',
        quality = 0.95,
        width = 0,
        height = 0,
        useCanvas = false
    } = {}) {
        width = Math.round(width);
        height = Math.round(height);
        // uc 不支持
        const CAN_RESIZE_BY_PICA = !navigator.userAgent.includes('UCBrowser');
        if(useCanvas || !CAN_RESIZE_BY_PICA) {
            return Promise.try(() => {
                return loadImage(img);
            })
                .then(img => {
                    const imgWidth = img.naturalWidth;
                    const imgHeight = img.naturalHeight;
                    const mime = `image/${type === 'png' ? 'png' : 'jpeg'}`;

                    if(!imgWidth || !imgHeight) {
                        throw new Error('Image load error or is empty');
                    }

                    if(!height) {
                        height = Math.round(imgHeight * width / imgWidth);
                    }
                    else if(!width) {
                        width = Math.round(imgWidth * height / imgHeight);
                    }

                    const canvas = document.createElement('canvas');
                    const ctx = canvas.getContext('2d');

                    canvas.height = height;
                    canvas.width = width;

                    ctx.drawImage(img, 0, 0, width, height);

                    const dataURL = canvas.toDataURL(mime, quality);

                    const blob = dataURLtoBlob(dataURL);
                    blob.resize = {
                        width: Math.floor(width),
                        height: Math.floor(height)
                    };
                    return blob;
                });
        }

        return resizeByPica(img, {
            type,
            quality,
            width,
            height,
            alpha: true
        });
    },
    resizeImageIfNeed(img, {
        type = 'jpeg',
        quality = 0.95,
        onProgress = null,
        useCanvas = false,
        maxSize = 2000 * 2000
    }) {
        return Promise.try(() => {
            if(typeof img === 'string') {
                const url = img;
                img = new Image();
                img.crossOrigin = 'anonymous';

                img.src = url;
                return loadImage(img);
            }

            return loadImage(img);
        })
            .then(img => {
                let width = Math.round(img.naturalWidth);
                let height = Math.round(img.naturalHeight);

                if(width === 0 || height === 0) return img;

                const [w, h] = this.getResizeSize(width, height, { maxSize }) || [width, height];
                const option = {
                    type,
                    quality,
                    onProgress,
                    useCanvas,
                    width: w,
                    height: h
                };

                return this.resizeImage(img, option).then(result => {
                    result.info = result.info || {};
                    Object.assign(result.info, {
                        width: Math.round(w),
                        height: Math.round(h)
                    });
                    return result;
                });
            });
    },
    clipImage(img, { ratio, mime }) {
        return Promise.try(() => {
            if(typeof img === 'string') {
                const url = img;
                img = new Image();
                img.crossOrigin = 'anonymous';

                img.src = url;
            }
            return loadImage(img);
        })
            .then(img => {
                const [w, h] = ratio.split(':');
                const resolvedRatio = w / h;
                const naturalRatio = img.naturalWidth / img.naturalHeight;
                let width = 0;
                let height = 0;
                let sx = 0;
                let sy = 0;

                if(resolvedRatio < naturalRatio) {
                    height = img.naturalHeight;
                    width = Math.floor(height * resolvedRatio);
                    sx = Math.floor((img.naturalWidth - width) / 2);
                }
                else {
                    width = img.naturalWidth;
                    height = Math.floor(width / resolvedRatio);
                    sy = Math.floor((img.naturalHeight - height) / 2);
                }

                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');

                canvas.height = height;
                canvas.width = width;

                ctx.drawImage(img, sx, sy, width, height, 0, 0, width, height);

                const dataURL = canvas.toDataURL(mime, 1);
                const newImg = new Image();
                newImg.crossOrigin = '';
                newImg.src = dataURL;

                return loadImage(newImg);
            });
    },

    resizeImageByFile(file, options = {}) {
        return Promise.try(() => {
            return readFileToImage(file);
        })
            .then(img => {
                return this.resizeImage(img, options);
            });
    },
    resizeImageByFileIfNeed(file, options = {}) {
        return Promise.try(() => {
            return readFileToImage(file);
        })
            .then(img => {
                return this.resizeImageIfNeed(img, options);
            });
    }
};

export default imageResizer;
